import { useState, useRef, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';

interface ImageViewerProps {
  imageUrl: string;
  imageName?: string;
  onClose: () => void;
}

export function ImageViewer({ imageUrl, imageName, onClose }: ImageViewerProps) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [initialScale, setInitialScale] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const containerWidth = containerRef.current?.clientWidth || window.innerWidth;
    const containerHeight = containerRef.current?.clientHeight || window.innerHeight;

    // Account for header and controls (approx 150px total)
    const availableHeight = containerHeight - 150;
    const availableWidth = containerWidth - 40; // Some padding

    // Calculate scale to fit image in viewport
    const scaleX = availableWidth / img.naturalWidth;
    const scaleY = availableHeight / img.naturalHeight;
    const fitScale = Math.min(scaleX, scaleY, 1); // Don't scale up, only down

    setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    setInitialScale(fitScale);
    setScale(fitScale);
    setPosition({ x: 0, y: 0 });
  };

  const constrainPosition = (newPosition: { x: number; y: number }, currentScale: number) => {
    if (!containerRef.current || !imageDimensions.width) return newPosition;

    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const imageWidth = imageDimensions.width * currentScale;
    const imageHeight = imageDimensions.height * currentScale;

    // Calculate max allowed translation
    const maxX = Math.max(0, (imageWidth - containerWidth) / 2);
    const maxY = Math.max(0, (imageHeight - containerHeight) / 2);

    return {
      x: Math.max(-maxX, Math.min(maxX, newPosition.x)),
      y: Math.max(-maxY, Math.min(maxY, newPosition.y)),
    };
  };

  const handleZoomIn = () => {
    setScale((prev) => {
      const newScale = Math.min(prev + 0.5, 5);
      setPosition((pos) => constrainPosition(pos, newScale));
      return newScale;
    });
  };

  const handleZoomOut = () => {
    setScale((prev) => {
      const newScale = Math.max(prev - 0.5, initialScale);
      if (newScale <= initialScale) {
        setPosition({ x: 0, y: 0 });
        return initialScale;
      }
      setPosition((pos) => constrainPosition(pos, newScale));
      return newScale;
    });
  };

  const handleReset = () => {
    setScale(initialScale);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > initialScale) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > initialScale) {
      e.preventDefault();
      const newPosition = {
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      };
      setPosition(constrainPosition(newPosition, scale));
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && scale > initialScale) {
      e.preventDefault();
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && isDragging && scale > initialScale) {
      e.preventDefault();
      const touch = e.touches[0];
      const newPosition = {
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y,
      };
      setPosition(constrainPosition(newPosition, scale));
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center"
      onClick={handleBackdropClick}
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 bg-black bg-opacity-50 p-4 flex items-center justify-between z-10">
        <div className="text-white font-medium">{imageName || 'Image'}</div>
        <button
          onClick={onClose}
          className="text-white hover:text-gray-300 p-2 rounded-full hover:bg-white hover:bg-opacity-10"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Controls */}
      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-4 flex items-center justify-center gap-4 z-10">
        <button
          onClick={handleZoomOut}
          disabled={scale <= initialScale}
          className="text-white hover:text-gray-300 p-2 rounded-full hover:bg-white hover:bg-opacity-10 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Zoom Out"
        >
          <MagnifyingGlassMinusIcon className="w-6 h-6" />
        </button>

        <div className="text-white text-sm font-mono bg-white bg-opacity-20 px-3 py-1 rounded">
          {Math.round((scale / initialScale) * 100)}%
        </div>

        <button
          onClick={handleZoomIn}
          disabled={scale >= 5}
          className="text-white hover:text-gray-300 p-2 rounded-full hover:bg-white hover:bg-opacity-10 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Zoom In"
        >
          <MagnifyingGlassPlusIcon className="w-6 h-6" />
        </button>

        <button
          onClick={handleReset}
          disabled={scale === initialScale}
          className="text-white hover:text-gray-300 p-2 rounded-full hover:bg-white hover:bg-opacity-10 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Reset"
        >
          <ArrowsPointingOutIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Image Container */}
      <div
        ref={containerRef}
        className="relative w-full h-full flex items-center justify-center overflow-hidden"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onWheel={handleWheel}
        style={{
          cursor: scale > initialScale ? (isDragging ? 'grabbing' : 'grab') : 'default',
          touchAction: 'none',
        }}
      >
        <img
          ref={imageRef}
          src={imageUrl}
          alt={imageName || 'Image'}
          onLoad={handleImageLoad}
          className="select-none"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: imageDimensions.width ? `${imageDimensions.width * initialScale}px` : 'auto',
            height: imageDimensions.height ? `${imageDimensions.height * initialScale}px` : 'auto',
            transform: `scale(${scale / initialScale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
            transition: isDragging ? 'none' : 'transform 0.2s ease-out',
          }}
          draggable={false}
        />
      </div>

      {/* Instructions */}
      <div className="absolute top-20 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white text-sm px-4 py-2 rounded pointer-events-none">
        {scale > initialScale ? 'Drag to pan • Scroll/pinch to zoom' : 'Scroll, pinch, or use buttons to zoom'}
      </div>
    </div>
  );
}
